import React from 'react';
import { motion } from 'framer-motion';
import { Package, Briefcase, Luggage, CalendarDays, MessageCircle, Check } from 'lucide-react';

const LOCKER_SIZES = [
  {
    id: 'small',
    name: 'S',
    label: 'Small',
    price: 1.5,
    dimensions: '28 × 50 × 43 cm',
    fits: 'Zaino, borsa a mano, laptop bag',
    icon: Package,
    quantity: 5,
  },
  {
    id: 'medium',
    name: 'M',
    label: 'Medium',
    price: 2.0,
    dimensions: '65 × 38 × 60 cm',
    fits: 'Trolley cabina (55×35×25), borse medie',
    icon: Briefcase,
    quantity: 12,
  },
  {
    id: 'xlarge',
    name: 'XL',
    label: 'Extra Large',
    price: 3.0,
    dimensions: '97 × 45 × 60 cm',
    fits: 'Valigie grandi, borsoni, attrezzatura sportiva',
    icon: Luggage,
    quantity: 8,
  },
];

const PricingSection = ({ selectedSize, setSelectedSize, onBookClick }) => {
  return (
    <section id="prezzi" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="text-[#FFCC00] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">
            Taglie & Prezzi
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Scegli il tuo locker
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Tre taglie per ogni esigenza. Prezzi trasparenti, nessun costo nascosto.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {LOCKER_SIZES.map((locker, index) => {
            const IconComponent = locker.icon;
            const isSelected = selectedSize === locker.id;
            
            return (
              <motion.div
                key={locker.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedSize(locker.id)}
                data-testid={`locker-${locker.id}`}
                className={`relative cursor-pointer bg-[#0A0A0A] border p-8 transition-all duration-300 ${
                  isSelected
                    ? 'border-[#FFCC00] shadow-[0_0_30px_rgba(255,204,0,0.15)]'
                    : 'border-[#2A2A2A] hover:border-[#FFCC00]/50'
                }`}
              >
                {/* Popular badge for Medium */}
                {locker.id === 'medium' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#FFCC00] text-[#0A0A0A] text-xs font-bold uppercase tracking-wider px-4 py-1">
                      Più Scelto
                    </span>
                  </div>
                )}

                <div className="flex flex-col items-center text-center">
                  {/* Icon */}
                  <div className={`w-16 h-16 flex items-center justify-center mb-6 ${isSelected ? 'text-[#FFCC00]' : 'text-white'}`}>
                    <IconComponent className="w-12 h-12" strokeWidth={1.5} />
                  </div>

                  {/* Size name */}
                  <h3 className="font-heading text-4xl font-bold text-white mb-2">
                    {locker.name}
                  </h3>
                  <p className="text-gray-400 text-sm uppercase tracking-wider mb-4">
                    {locker.label}
                  </p>

                  {/* Price */}
                  <div className="mb-4">
                    <span className="text-[#FFCC00] text-4xl font-bold">
                      €{locker.price.toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-gray-500 text-lg">/ora</span>
                  </div>

                  {/* Dimensions */}
                  <div className="bg-[#141414] border border-[#2A2A2A] px-4 py-2 mb-4 w-full">
                    <p className="text-[#FFCC00] text-sm font-semibold mb-1">Dimensioni</p>
                    <p className="text-white text-sm font-mono">{locker.dimensions}</p>
                    <p className="text-gray-500 text-xs mt-1">(A × L × P)</p>
                  </div>

                  {/* What fits */}
                  <div className="mb-4">
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Cosa ci sta:</p>
                    <p className="text-gray-300 text-sm">
                      {locker.fits}
                    </p>
                  </div>

                  {/* Availability */}
                  <p className="text-gray-600 text-xs">
                    {locker.quantity} locker disponibili
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Book CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <button
            onClick={onBookClick}
            data-testid="book-locker-cta"
            className="inline-flex items-center justify-center gap-3 bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide hover:bg-[#E6B800] px-10 py-5 text-lg transition-all duration-200"
          >
            <CalendarDays className="w-6 h-6" />
            Prenota Online
          </button>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
            <a
              href={`https://wa.me/393773781602?text=Ciao! Vorrei prenotare un locker taglia ${selectedSize.toUpperCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-whatsapp-cta"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-[#25D366] transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Oppure su WhatsApp
            </a>
            <span className="hidden sm:block text-gray-600">|</span>
            <a href="tel:+393773781602" className="text-gray-400 hover:text-[#FFCC00] transition-colors">
              +39 377 378 1602
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
